import React, { useState } from 'react';
import { useCoffee } from '../context/CoffeeContext';
import { ChevronDownIcon, ChevronUpIcon, TrashIcon, EditIcon, PlusIcon, AlertCircleIcon } from 'lucide-react';
import { CoffeeBag } from '../types';
export const BeansList: React.FC = () => {
  const {
    coffeeBags,
    coffeeEntries,
    addCoffeeBag,
    deleteCoffeeBag
  } = useCoffee();
  const [expandedBag, setExpandedBag] = useState<string | null>(null);
  const [editingBag, setEditingBag] = useState<string | null>(null);
  const [showNewBagForm, setShowNewBagForm] = useState(false);
  const [bagForm, setBagForm] = useState<Omit<CoffeeBag, 'id'>>({
    name: '',
    roaster: '',
    origin: '',
    process: '',
    brand: '',
    roastLevel: 'Medium',
    roastDate: '',
    purchaseDate: '',
    notes: ''
  });
  const toggleExpand = (id: string) => {
    setExpandedBag(expandedBag === id ? null : id);
  };
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };
  const isOldCoffee = (roastDate: string) => {
    if (!roastDate) return false;
    const today = new Date();
    const roastDateObj = new Date(roastDate);
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    return roastDateObj < threeMonthsAgo;
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setBagForm({
      ...bagForm,
      [name]: value
    });
  };
  const handleEditBag = (bag: CoffeeBag) => {
    setEditingBag(bag.id);
    setBagForm({
      name: bag.name,
      roaster: bag.roaster,
      origin: bag.origin,
      process: bag.process || '',
      roastLevel: bag.roastLevel,
      roastDate: bag.roastDate,
      purchaseDate: bag.purchaseDate,
      notes: bag.notes
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingBag) {
      // In a real app, you'd update the bag here
      // For now, we'll just show an alert
      alert('Funcionalidad de edición no implementada en esta demo');
      setEditingBag(null);
    } else {
      const bagId = Date.now().toString();
      addCoffeeBag({
        ...bagForm,
        id: bagId
      });
      setShowNewBagForm(false);
    }
    // Reset form
    setBagForm({
      name: '',
      roaster: '',
      origin: '',
      process: '',
      roastLevel: 'Medium',
      roastDate: '',
      purchaseDate: '',
      notes: ''
    });
  };
  const canDeleteBag = (bagId: string) => {
    // Check if there are entries using this bag
    return !coffeeEntries.some(entry => entry.bagId === bagId);
  };
  const renderBagForm = () => <form onSubmit={handleSubmit} data-id="element-48">
      <div className="mb-3" data-id="element-49">
        <label className="block text-sm font-medium text-amber-700" data-id="element-50">
          Nombre
        </label>
        <input type="text" name="name" value={bagForm.name} onChange={handleInputChange} className="w-full px-3 py-2 border border-amber-300 rounded-md" required data-id="element-51" />
      </div>
      <div className="mb-3" data-id="element-52">
        <label className="block text-sm font-medium text-amber-700" data-id="element-53">
          Origen
        </label>
        <input type="text" name="origin" value={bagForm.origin} onChange={handleInputChange} className="w-full px-3 py-2 border border-amber-300 rounded-md" data-id="element-54" />
      </div>
      <div className="mb-3" data-id="element-55">
        <label className="block text-sm font-medium text-amber-700" data-id="element-56">
          Marca/Tienda
        </label>
        <input type="text" name="brand" value={bagForm.brand} onChange={handleInputChange} className="w-full px-3 py-2 border border-amber-300 rounded-md" placeholder="Tienda donde lo compraste" data-id="element-57" />
      </div>
      <div className="mb-3" data-id="element-58">
        <label className="block text-sm font-medium text-amber-700" data-id="element-59">
          Proceso
        </label>
        <select name="process" value={bagForm.process} onChange={handleInputChange} className="w-full px-3 py-2 border border-amber-300 rounded-md" data-id="element-60">
          <option value="" data-id="element-61">Seleccionar proceso</option>
          <option value="Lavado" data-id="element-62">Lavado</option>
          <option value="Natural" data-id="element-63">Natural</option>
          <option value="Honey" data-id="element-64">Honey</option>
          <option value="Anaeróbico" data-id="element-65">Anaeróbico</option>
          <option value="Fermentado" data-id="element-66">Fermentado</option>
          <option value="Otro" data-id="element-67">Otro</option>
        </select>
      </div>
      <div className="mb-3" data-id="element-68">
        <label className="block text-sm font-medium text-amber-700" data-id="element-69">
          Tostador
        </label>
        <input type="text" name="roaster" value={bagForm.roaster} onChange={handleInputChange} className="w-full px-3 py-2 border border-amber-300 rounded-md" data-id="element-70" />
      </div>
      <div className="mb-3" data-id="element-71">
        <label className="block text-sm font-medium text-amber-700" data-id="element-72">
          Nivel de Tueste
        </label>
        <select name="roastLevel" value={bagForm.roastLevel} onChange={handleInputChange} className="w-full px-3 py-2 border border-amber-300 rounded-md" data-id="element-73">
          <option value="Light" data-id="element-74">Ligero</option>
          <option value="Medium-Light" data-id="element-75">Medio-Ligero</option>
          <option value="Medium" data-id="element-76">Medio</option>
          <option value="Medium-Dark" data-id="element-77">Medio-Oscuro</option>
          <option value="Dark" data-id="element-78">Oscuro</option>
        </select>
      </div>
      <div className="mb-3" data-id="element-79">
        <label className="block text-sm font-medium text-amber-700" data-id="element-80">
          Fecha de Tueste
        </label>
        <input type="date" name="roastDate" value={bagForm.roastDate} onChange={handleInputChange} className="w-full px-3 py-2 border border-amber-300 rounded-md" data-id="element-81" />
      </div>
      <div className="mb-3" data-id="element-82">
        <label className="block text-sm font-medium text-amber-700" data-id="element-83">
          Fecha de Compra
        </label>
        <input type="date" name="purchaseDate" value={bagForm.purchaseDate} onChange={handleInputChange} className="w-full px-3 py-2 border border-amber-300 rounded-md" data-id="element-84" />
      </div>
      <div className="mb-3" data-id="element-85">
        <label className="block text-sm font-medium text-amber-700" data-id="element-86">
          Notas
        </label>
        <textarea name="notes" value={bagForm.notes} onChange={handleInputChange} className="w-full px-3 py-2 border border-amber-300 rounded-md" rows={3} data-id="element-87"></textarea>
      </div>
      <div className="flex space-x-2" data-id="element-88">
        <button type="submit" className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700" data-id="element-89">
          {editingBag ? 'Actualizar' : 'Guardar'}
        </button>
        <button type="button" onClick={() => {
        setShowNewBagForm(false);
        setEditingBag(null);
      }} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400" data-id="element-90">
          Cancelar
        </button>
      </div>
    </form>;
  if (coffeeBags.length === 0 && !showNewBagForm) {
    return <div className="max-w-md mx-auto" data-id="element-91">
        <h2 className="text-xl font-bold mb-4 text-amber-900" data-id="element-92">Mis Cafés</h2>
        <div className="bg-white p-4 rounded-lg shadow text-center" data-id="element-93">
          <p className="text-amber-800 font-bold mb-1" data-id="element-94">No tienes granos de café.</p>
          <p className="text-amber-800 mb-6" data-id="element-95">Agrega para poder registrar preparaciones.</p>
          <button onClick={() => setShowNewBagForm(true)} className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center mx-auto" data-id="element-96">
            <PlusIcon size={18} className="mr-1" data-id="element-97" />
            Añadir Café
          </button>
        </div>
      </div>;
  }
  return <div className="max-w-md mx-auto" data-id="element-98">
      <div className="flex justify-between items-center mb-4" data-id="element-99">
        <h2 className="text-xl font-bold text-amber-900" data-id="element-100">Mis Cafés</h2>
        {!showNewBagForm && !editingBag && <button onClick={() => setShowNewBagForm(true)} className="px-3 py-1 bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center" data-id="element-101">
            <PlusIcon size={18} className="mr-1" data-id="element-102" />
            Nuevo
          </button>}
      </div>
      {(showNewBagForm || editingBag) && <div className="bg-white p-4 rounded-lg shadow mb-4" data-id="element-103">
          <h3 className="text-lg font-semibold mb-2 text-amber-800" data-id="element-104">
            {editingBag ? 'Editar Café' : 'Nuevo Café'}
          </h3>
          {renderBagForm()}
        </div>}
      <div className="space-y-4" data-id="element-105">
        {coffeeBags.map(bag => <div key={bag.id} className="bg-white rounded-lg shadow overflow-hidden" data-id="element-106">
            <div className="p-4 flex justify-between items-center cursor-pointer" onClick={() => toggleExpand(bag.id)} data-id="element-107">
              <div className="flex-grow" data-id="element-108">
                <div className="flex items-center" data-id="element-109">
                  <h3 className="font-medium text-amber-800" data-id="element-110">{bag.name}</h3>
                  {isOldCoffee(bag.roastDate) && <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full flex items-center" data-id="element-111">
                      <AlertCircleIcon size={12} className="mr-1" data-id="element-112" />
                      Viejo
                    </span>}
                </div>
                <p className="text-sm text-gray-600" data-id="element-113">
                  {bag.process || 'Proceso no especificado'}
                  {bag.brand && ` • ${bag.brand}`}
                </p>
              </div>
              <div data-id="element-114">
                {expandedBag === bag.id ? <ChevronUpIcon className="text-amber-600" size={20} data-id="element-115" /> : <ChevronDownIcon className="text-amber-600" size={20} data-id="element-116" />}
              </div>
            </div>
            {expandedBag === bag.id && <div className="px-4 pb-4 border-t border-amber-100 pt-2" data-id="element-117">
                <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-118">
                  <div data-id="element-119">
                    <span className="text-xs text-amber-700" data-id="element-120">Origen</span>
                    <p className="font-medium" data-id="element-121">{bag.origin || '-'}</p>
                  </div>
                  <div data-id="element-122">
                    <span className="text-xs text-amber-700" data-id="element-123">
                      Nivel de Tueste
                    </span>
                    <p className="font-medium" data-id="element-124">{bag.roastLevel}</p>
                  </div>
                  <div data-id="element-125">
                    <span className="text-xs text-amber-700" data-id="element-126">Tostador</span>
                    <p className="font-medium" data-id="element-127">{bag.roaster || '-'}</p>
                  </div>
                  <div data-id="element-128">
                    <span className="text-xs text-amber-700" data-id="element-129">Proceso</span>
                    <p className="font-medium" data-id="element-130">{bag.process || '-'}</p>
                  </div>
                  <div data-id="element-131">
                    <span className="text-xs text-amber-700" data-id="element-132">Marca/Tienda</span>
                    <p className="font-medium" data-id="element-133">{bag.brand || '-'}</p>
                  </div>
                  <div data-id="element-134">
                    <span className="text-xs text-amber-700" data-id="element-135">
                      Fecha de Tueste
                    </span>
                    <p className="font-medium" data-id="element-136">{formatDate(bag.roastDate)}</p>
                  </div>
                  <div data-id="element-137">
                    <span className="text-xs text-amber-700" data-id="element-138">
                      Fecha de Compra
                    </span>
                    <p className="font-medium" data-id="element-139">
                      {formatDate(bag.purchaseDate)}
                    </p>
                  </div>
                </div>
                {bag.notes && <div className="mb-3" data-id="element-140">
                    <span className="text-xs text-amber-700" data-id="element-141">Notas</span>
                    <p className="text-sm whitespace-pre-line" data-id="element-142">{bag.notes}</p>
                  </div>}
                <div className="flex justify-end mt-2 space-x-2" data-id="element-143">
                  <button onClick={e => {
              e.stopPropagation();
              handleEditBag(bag);
            }} className="flex items-center text-amber-600 px-3 py-1 hover:bg-amber-50 rounded-md" data-id="element-144">
                    <EditIcon size={16} className="mr-1" data-id="element-145" />
                    <span data-id="element-146">Editar</span>
                  </button>
                  <button onClick={e => {
              e.stopPropagation();
              if (canDeleteBag(bag.id)) {
                if (confirm('¿Estás seguro de que quieres eliminar este café?')) {
                  deleteCoffeeBag(bag.id);
                }
              } else {
                alert('No se puede eliminar este café porque tiene preparaciones registradas.');
              }
            }} className={`flex items-center px-3 py-1 rounded-md ${canDeleteBag(bag.id) ? 'text-red-500 hover:bg-red-50' : 'text-gray-400 cursor-not-allowed'}`} disabled={!canDeleteBag(bag.id)} data-id="element-147">
                    <TrashIcon size={16} className="mr-1" data-id="element-148" />
                    <span data-id="element-149">Eliminar</span>
                  </button>
                </div>
              </div>}
          </div>)}
      </div>
    </div>;
};